import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaToggleOn, FaToggleOff, FaSync } from 'react-icons/fa';

const IndstillingerEftersynsskema = () => {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [fields, setFields] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [editingCategoryVehicleTypes, setEditingCategoryVehicleTypes] = useState([]);
  const [editingVehicleTypeInput, setEditingVehicleTypeInput] = useState('');
  const [labelColor, setLabelColor] = useState('#2563eb');
  const [settingsMessage, setSettingsMessage] = useState('');
  const [settingsError, setSettingsError] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);

  // Form states
  const [newCategory, setNewCategory] = useState('');
  const [newCategoryVehicleTypes, setNewCategoryVehicleTypes] = useState([]);
  const [newCategoryVehicleTypeInput, setNewCategoryVehicleTypeInput] = useState('');
  const [newSubcategory, setNewSubcategory] = useState('');
  const [fieldForm, setFieldForm] = useState({
    label: '',
    name: '',
    type: 'text',
    options: [],
    sort_order: 0,
    repeatable: false
  });

  // Options management for select fields
  const [newOption, setNewOption] = useState('');

  useEffect(() => {
    fetchCategories();
    fetchVehicleTypes();
    fetchEftersynsskemaSettings();
  }, []);

  useEffect(() => {
    if (selectedCategoryId) {
      fetchSubcategories(selectedCategoryId);
    }
  }, [selectedCategoryId]);

  useEffect(() => {
    if (selectedSubcategoryId) {
      fetchFields(selectedSubcategoryId);
    }
  }, [selectedSubcategoryId]);

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/indstillinger/eftersynsskema/categories');
      const data = await response.json();
      const normalized = Array.isArray(data)
        ? data.map(cat => ({
            ...cat,
            vehicle_types: Array.isArray(cat.vehicle_types) ? cat.vehicle_types : []
          }))
        : [];
      setCategories(normalized);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchVehicleTypes = async () => {
    try {
      const response = await fetch('/api/indstillinger/eftersynsskema/vehicle-types');
      const data = await response.json();
      if (Array.isArray(data)) {
        setVehicleTypes(data);
      }
    } catch (error) {
      console.error('Error fetching vehicle types:', error);
    }
  };

  const fetchSubcategories = async (categoryId) => {
    try {
      const response = await fetch(`/api/indstillinger/eftersynsskema/categories/${categoryId}/subcategories`);
      const data = await response.json();
      setSubcategories(data);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  const fetchFields = async (subcategoryId) => {
    try {
      const response = await fetch(`/api/indstillinger/eftersynsskema/subcategories/${subcategoryId}/fields`);
      const data = await response.json();
      setFields(data);
    } catch (error) {
      console.error('Error fetching fields:', error);
    }
  };

  const fetchEftersynsskemaSettings = async () => {
    try {
      const response = await fetch('/api/indstillinger/eftersynsskema/settings');
      const data = await response.json();
      if (data?.label_color) {
        setLabelColor(data.label_color);
      }
    } catch (error) {
      console.error('Error fetching eftersynsskema settings:', error);
    }
  };

  const handleSaveSettings = async () => {
    setSettingsMessage('');
    setSettingsError('');
    setSettingsSaving(true);
    try {
      const response = await fetch('/api/indstillinger/eftersynsskema/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label_color: labelColor })
      });
      const data = await response.json();
      if (!response.ok) {
        setSettingsError(data?.message || 'Kunne ikke gemme indstillinger');
        return;
      }
      setSettingsMessage('Indstillinger gemt!');
      if (data?.settings?.label_color) {
        setLabelColor(data.settings.label_color);
      }
    } catch (error) {
      console.error('Error saving eftersynsskema settings:', error);
      setSettingsError('Fejl ved gemning af indstillinger');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/indstillinger/eftersynsskema/categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCategory, vehicle_types: newCategoryVehicleTypes })
      });

      if (response.ok) {
        setNewCategory('');
        setNewCategoryVehicleTypes([]);
        setNewCategoryVehicleTypeInput('');
        fetchCategories();
      }
    } catch (error) {
      console.error('Error creating category:', error);
    }
  };

  const handleStartEditCategory = (category) => {
    setEditingCategoryId(category.id);
    setEditingCategoryName(category.name);
    setEditingCategoryVehicleTypes(category.vehicle_types || []);
    setEditingVehicleTypeInput('');
  };

  const handleCancelEditCategory = () => {
    setEditingCategoryId(null);
    setEditingCategoryName('');
    setEditingCategoryVehicleTypes([]);
    setEditingVehicleTypeInput('');
  };

  const handleUpdateCategory = async (categoryId) => {
    try {
      const response = await fetch(`/api/indstillinger/eftersynsskema/categories/${categoryId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editingCategoryName, vehicle_types: editingCategoryVehicleTypes })
      });

      if (response.ok) {
        handleCancelEditCategory();
        fetchCategories();
      }
    } catch (error) {
      console.error('Error updating category:', error);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('Slet kategori og alt indhold?')) {
      try {
        const response = await fetch(`/api/indstillinger/eftersynsskema/categories/${categoryId}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          if (selectedCategoryId === categoryId) {
            setSelectedCategoryId(null);
            setSelectedSubcategoryId(null);
          }
          fetchCategories();
        }
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  const handleCreateSubcategory = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/indstillinger/eftersynsskema/subcategories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          category_id: selectedCategoryId, 
          name: newSubcategory 
        })
      });
      
      if (response.ok) {
        setNewSubcategory('');
        fetchSubcategories(selectedCategoryId);
      }
    } catch (error) {
      console.error('Error creating subcategory:', error);
    }
  };

  const handleDeleteSubcategory = async (subcategoryId) => {
    if (window.confirm('Slet underkategori og alt indhold?')) {
      try {
        const response = await fetch(`/api/indstillinger/eftersynsskema/subcategories/${subcategoryId}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          if (selectedSubcategoryId === subcategoryId) {
            setSelectedSubcategoryId(null);
          }
          fetchSubcategories(selectedCategoryId);
        }
      } catch (error) {
        console.error('Error deleting subcategory:', error);
      }
    }
  };

  const handleCreateField = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/indstillinger/eftersynsskema/fields', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...fieldForm,
          subcategory_id: selectedSubcategoryId
        })
      });
      
      if (response.ok) {
        resetFieldForm();
        fetchFields(selectedSubcategoryId);
      }
    } catch (error) {
      console.error('Error creating field:', error);
    }
  };

  const handleUpdateField = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`/api/indstillinger/eftersynsskema/fields/${editingField.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fieldForm)
      });
      
      if (response.ok) {
        setEditingField(null);
        resetFieldForm();
        fetchFields(selectedSubcategoryId);
      }
    } catch (error) {
      console.error('Error updating field:', error);
    }
  };

  const handleDeleteField = async (fieldId) => {
    if (window.confirm('Slet feltet?')) {
      try {
        const response = await fetch(`/api/indstillinger/eftersynsskema/fields/${fieldId}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          fetchFields(selectedSubcategoryId);
        }
      } catch (error) {
        console.error('Error deleting field:', error);
      }
    }
  };

  const handleEditField = (field) => {
    setEditingField(field);
    setFieldForm({
      label: field.label,
      name: field.name,
      type: field.type,
      options: field.options ? JSON.parse(field.options) : [],
      sort_order: field.sort_order,
      repeatable: field.repeatable === 1
    });
  };

  const handleToggleRepeatable = async (fieldId, currentRepeatable) => {
    try {
      const response = await fetch(`/api/indstillinger/eftersynsskema/fields/${fieldId}/repeatable`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ repeatable: !currentRepeatable })
      });
      
      if (response.ok) {
        fetchFields(selectedSubcategoryId);
      }
    } catch (error) {
      console.error('Error toggling repeatable:', error);
    }
  };

  const handleRepeatAllFields = async () => {
    try {
      const response = await fetch(`/api/indstillinger/eftersynsskema/subcategories/${selectedSubcategoryId}/repeat-all`, {
        method: 'PATCH'
      });
      
      if (response.ok) {
        fetchFields(selectedSubcategoryId);
      }
    } catch (error) {
      console.error('Error repeating all fields:', error);
    }
  };

  const resetFieldForm = () => {
    setFieldForm({
      label: '',
      name: '',
      type: 'text',
      options: [],
      sort_order: 0,
      repeatable: false
    });
    setNewOption('');
  };

  const addOption = () => {
    if (newOption.trim()) {
      setFieldForm(prev => ({
        ...prev,
        options: [...prev.options, newOption.trim()]
      }));
      setNewOption('');
    }
  };

  const addVehicleType = (value, currentList, setList) => {
    const trimmed = (value || '').trim();
    if (!trimmed) return;
    const exists = currentList.some((type) => type.toLowerCase() === trimmed.toLowerCase());
    if (!exists) {
      setList([...currentList, trimmed]);
    }
  };

  const addAllVehicleTypes = (currentList, setList) => {
    if (!Array.isArray(vehicleTypes) || vehicleTypes.length === 0) return;
    const normalizedCurrent = currentList.map((type) => type.toLowerCase());
    const merged = [
      ...currentList,
      ...vehicleTypes.filter((type) => !normalizedCurrent.includes(type.toLowerCase()))
    ];
    setList(merged);
  };

  const removeVehicleType = (type, currentList, setList) => {
    setList(currentList.filter((item) => item !== type));
  };

  const removeOption = (index) => {
    setFieldForm(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const handleFieldSubmit = editingField ? handleUpdateField : handleCreateField;

  return (
    <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Eftersynsskema – administration</h1>

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Udseende på serviceskema</h2>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">Label-farve</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={labelColor}
                onChange={(e) => setLabelColor(e.target.value)}
                className="h-10 w-14 border border-gray-300 rounded"
              />
              <input
                type="text"
                value={labelColor}
                onChange={(e) => setLabelColor(e.target.value)}
                className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-falck-blue"
                placeholder="#2563eb"
              />
            </div>
            <p className="text-xs text-gray-500">Farven bruges til teksten “Alt serviceret materiel er markeret med gul/blå label.”</p>
          </div>
          <div className="sm:ml-auto">
            <button
              type="button"
              onClick={handleSaveSettings}
              disabled={settingsSaving}
              className={`px-4 py-2 rounded-lg text-white ${settingsSaving ? 'bg-gray-400' : 'bg-falck-blue hover:bg-falck-dark-blue'}`}
            >
              {settingsSaving ? 'Gemmer...' : 'Gem indstillinger'}
            </button>
          </div>
        </div>
        {settingsMessage && <p className="text-sm text-green-600 mt-3">{settingsMessage}</p>}
        {settingsError && <p className="text-sm text-red-600 mt-3">{settingsError}</p>}
      </div>

      {/* Opret kategori */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Opret ny kategori</h2>
          <form onSubmit={handleCreateCategory} className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4">
              <div className="w-full sm:flex-1">
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Kategorinavn"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-falck-blue"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full sm:w-auto bg-falck-blue text-white px-4 py-2 rounded-lg hover:bg-falck-dark-blue transition-colors"
              >
                Opret kategori
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Vogn typer (valgfrit)</label>
              <div className="flex flex-col sm:flex-row sm:items-stretch gap-2 mb-3">
                <input
                  type="text"
                  value={newCategoryVehicleTypeInput}
                  onChange={(e) => setNewCategoryVehicleTypeInput(e.target.value)}
                  placeholder="Tilføj vogn type"
                  list="vehicle-type-suggestions"
                  className="w-full sm:flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-falck-blue"
                />
                <button
                  type="button"
                  onClick={() => {
                    addVehicleType(newCategoryVehicleTypeInput, newCategoryVehicleTypes, setNewCategoryVehicleTypes);
                    setNewCategoryVehicleTypeInput('');
                  }}
                  className="w-full sm:w-auto bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center gap-2"
                >
                  <FaPlus className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => addAllVehicleTypes(newCategoryVehicleTypes, setNewCategoryVehicleTypes)}
                  className="w-full sm:w-auto bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Tilføj alle
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {newCategoryVehicleTypes.map((type) => (
                  <span key={type} className="bg-gray-200 text-gray-800 px-3 py-1 rounded-full flex items-center gap-2">
                    {type}
                    <button
                      type="button"
                      onClick={() => removeVehicleType(type, newCategoryVehicleTypes, setNewCategoryVehicleTypes)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <FaTimes className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </form>

          <datalist id="vehicle-type-suggestions">
            {vehicleTypes.map((type) => (
              <option key={type} value={type} />
            ))}
          </datalist>

          <ul className="mt-4 space-y-2">
            {categories.map(cat => (
              <li
                key={cat.id}
                className="p-3 bg-gray-50 rounded-lg space-y-3"
              >
                {editingCategoryId === cat.id ? (
                  <div className="space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <input
                        type="text"
                        value={editingCategoryName}
                        onChange={(e) => setEditingCategoryName(e.target.value)}
                        className="w-full sm:flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-falck-blue"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Vogn typer</label>
                      <div className="flex flex-col sm:flex-row sm:items-stretch gap-2 mb-3">
                        <input
                          type="text"
                          value={editingVehicleTypeInput}
                          onChange={(e) => setEditingVehicleTypeInput(e.target.value)}
                          placeholder="Tilføj vogn type"
                          list="vehicle-type-suggestions"
                          className="w-full sm:flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-falck-blue"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            addVehicleType(editingVehicleTypeInput, editingCategoryVehicleTypes, setEditingCategoryVehicleTypes);
                            setEditingVehicleTypeInput('');
                          }}
                          className="w-full sm:w-auto bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center gap-2"
                        >
                          <FaPlus className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => addAllVehicleTypes(editingCategoryVehicleTypes, setEditingCategoryVehicleTypes)}
                          className="w-full sm:w-auto bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                        >
                          Tilføj alle
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {editingCategoryVehicleTypes.map((type) => (
                          <span key={type} className="bg-gray-200 text-gray-800 px-3 py-1 rounded-full flex items-center gap-2">
                            {type}
                            <button
                              type="button"
                              onClick={() => removeVehicleType(type, editingCategoryVehicleTypes, setEditingCategoryVehicleTypes)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <FaTimes className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() => handleUpdateCategory(cat.id)}
                        className="w-full sm:w-auto bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <FaSave className="h-4 w-4" /> Gem
                      </button>
                      <button
                        onClick={handleCancelEditCategory}
                        className="w-full sm:w-auto bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                      >
                        Annullér
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="space-y-2">
                      <span className="font-medium break-words block">{cat.name}</span>
                      <div className="flex flex-wrap gap-2">
                        {(cat.vehicle_types || []).map((type) => (
                          <span key={type} className="bg-gray-200 text-gray-800 px-3 py-1 rounded-full text-sm">
                            {type}
                          </span>
                        ))}
                        {(cat.vehicle_types || []).length === 0 && (
                          <span className="text-sm text-gray-500">Ingen vogn typer tilknyttet</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleStartEditCategory(cat)}
                        className="text-blue-600 hover:text-blue-800 p-2"
                        title="Rediger kategori"
                      >
                        <FaEdit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('Slet kategori og alt indhold?')) handleDeleteCategory(cat.id);
                        }}
                        className="text-red-600 hover:text-red-800 p-2"
                        title="Slet kategori"
                      >
                        <FaTrash className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
      </div>

      {/* Vælg kategori og opret underkategori */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Vælg kategori og opret ny underkategori</h2>
        
        <div className="mb-4">
          <select
            value={selectedCategoryId || ''}
            onChange={(e) => {
              setSelectedCategoryId(e.target.value ? parseInt(e.target.value) : null);
              setSelectedSubcategoryId(null);
            }}
            className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-falck-blue"
            required
          >
            <option value="">-- Vælg kategori --</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

          {selectedCategoryId && (
            <form onSubmit={handleCreateSubcategory} className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4 mb-4">
              <div className="w-full sm:flex-1">
                <input
                  type="text"
                  value={newSubcategory}
                  onChange={(e) => setNewSubcategory(e.target.value)}
                  placeholder="Underkategori-navn"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-falck-blue"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full sm:w-auto bg-falck-blue text-white px-4 py-2 rounded-lg hover:bg-falck-dark-blue transition-colors"
              >
                Opret underkategori
              </button>
            </form>
        )}

          {selectedCategoryId && (
            <div className="mb-4">
              <select
                value={selectedSubcategoryId || ''}
                onChange={(e) => setSelectedSubcategoryId(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-falck-blue"
              >
              <option value="">-- Vælg underkategori --</option>
              {subcategories.map(sub => (
                <option key={sub.id} value={sub.id}>{sub.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Opret/rediger felt */}
      {selectedSubcategoryId && (
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingField ? 'Rediger felt' : 'Tilføj nyt felt'}
          </h3>
          
          <form onSubmit={handleFieldSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <input
                type="text"
                value={fieldForm.label}
                onChange={(e) => setFieldForm(prev => ({ ...prev, label: e.target.value }))}
                placeholder="Label"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-falck-blue"
                required
              />
              <input
                type="text"
                value={fieldForm.name}
                onChange={(e) => setFieldForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Navn (HTML name)"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-falck-blue"
                required
              />
              <select
                value={fieldForm.type}
                onChange={(e) => setFieldForm(prev => ({ ...prev, type: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-falck-blue"
                required
              >
                <option value="text">Text</option>
                <option value="textarea">Textarea</option>
                <option value="date">Date</option>
                <option value="select">Select</option>
                <option value="checkbox">Checkbox</option>
              </select>
              <input
                type="number"
                value={fieldForm.sort_order}
                onChange={(e) => setFieldForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) }))}
                placeholder="Sortering"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-falck-blue"
              />
            </div>

            {/* Options for select fields */}
              {fieldForm.type === 'select' && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row sm:items-stretch gap-2 mb-3">
                  <input
                    type="text"
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    placeholder="Tilføj valgmulighed"
                      className="w-full sm:flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-falck-blue"
                  />
                    <button
                      type="button"
                      onClick={addOption}
                      className="w-full sm:w-auto bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center gap-2"
                    >
                    <FaPlus className="h-4 w-4" />
                  </button>
                </div>
                
                  <div className="flex flex-wrap gap-2">
                  {fieldForm.options.map((option, index) => (
                    <span key={index} className="bg-gray-200 text-gray-800 px-3 py-1 rounded-full flex items-center gap-2">
                      {option}
                      <button
                        type="button"
                        onClick={() => removeOption(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <FaTimes className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={fieldForm.repeatable}
                  onChange={(e) => setFieldForm(prev => ({ ...prev, repeatable: e.target.checked }))}
                  className="rounded border-gray-300 text-falck-blue focus:ring-falck-blue"
                />
                <span>Gentageligt felt</span>
              </label>
            </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                type="submit"
                  className={`w-full sm:w-auto px-4 py-2 rounded-lg text-white transition-colors ${
                  editingField 
                    ? 'bg-yellow-600 hover:bg-yellow-700' 
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {editingField ? (
                  <>
                    <FaSave className="inline mr-2" />
                    Gem ændringer
                  </>
                ) : (
                  <>
                    <FaPlus className="inline mr-2" />
                    Tilføj felt
                  </>
                )}
              </button>
              
                {editingField && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingField(null);
                      resetFieldForm();
                    }}
                    className="w-full sm:w-auto bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Annullér
                  </button>
                )}
            </div>
          </form>

          {/* Gentag alle */}
          <button
            onClick={handleRepeatAllFields}
            className="mt-4 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
          >
            <FaSync className="h-4 w-4" />
            Gør alle felter gentagelige
          </button>

          {/* Liste over felter */}
            <h3 className="text-lg font-semibold mt-6 mb-4">Eksisterende felter</h3>
            <ul className="space-y-2">
              {fields.map(field => (
                <li
                  key={field.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <span className="font-medium break-words">
                      {field.label} ({field.type})
                    </span>
                    <label className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleRepeatable(field.id, field.repeatable)}
                        className={`text-lg ${field.repeatable ? 'text-green-600' : 'text-gray-400'}`}
                        title={field.repeatable ? 'Gør ikke gentageligt' : 'Gør gentageligt'}
                      >
                        {field.repeatable ? <FaToggleOn /> : <FaToggleOff />}
                      </button>
                      <span className="text-sm text-gray-600">Gentageligt</span>
                    </label>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={() => handleEditField(field)}
                      className="text-blue-600 hover:text-blue-800 p-2 flex items-center justify-center"
                      title="Rediger"
                    >
                      <FaEdit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm('Slet feltet?')) handleDeleteField(field.id);
                      }}
                      className="text-red-600 hover:text-red-800 p-2 flex items-center justify-center"
                      title="Slet"
                    >
                      <FaTrash className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
        </div>
      )}
    </div>
  );
};

export default IndstillingerEftersynsskema;
